import React, { useEffect, useState } from 'react';
import { API } from 'utilities/api';

/**
 * The social sign-in buttons, drawn from what the brand has actually set up.
 *
 * The server says, per provider, whether it works and whose app it is. Both
 * matter here. A button for a provider nobody configured is a button that
 * leads to an error page, and a button that implies the brand owns an app it
 * does not is the surprise this whole feature was built to avoid — the
 * customer will see "TechProOne" on the consent screen either way, so the
 * button says so first.
 *
 * Plain elements and inline styles: this renders on the brand's own domain,
 * outside any stylesheet of ours.
 */

const MARKS = {
  google: { label: 'Google', glyph: 'G', colour: '#ffffff', ink: '#1f1f1f', border: '#dadce0' },
  facebook: { label: 'Facebook', glyph: 'f', colour: '#1877F2', ink: '#ffffff', border: '#1877F2' },
};

export default function SocialButtons({ brandId, returnTo = '/account', isDark = false }) {
  const [providers, setProviders] = useState([]);

  useEffect(() => {
    let cancelled = false;
    API.publicAuthProviders()
      .then((res) => { if (!cancelled) setProviders(Array.isArray(res) ? res : []); })
      // No buttons rather than broken ones: password sign-in still works, and
      // a provider we could not ask about is one we cannot promise.
      .catch(() => { if (!cancelled) setProviders([]); });
    return () => { cancelled = true; };
  }, [brandId]);

  const usable = providers.filter((p) => p.available);
  if (usable.length === 0) return null;

  const muted = isDark ? 'rgba(229,231,235,0.65)' : '#64748b';
  const line = isDark ? 'rgba(255,255,255,0.14)' : '#e2e8f0';

  const start = (provider) => {
    // A full page navigation, not a fetch: the provider's consent screen is a
    // place the browser has to actually go.
    const qs = new URLSearchParams({ return_to: returnTo });
    window.location.assign(`/api/v1/public/auth/${provider}/start?${qs.toString()}`);
  };

  return (
    <div style={{ marginTop: 20 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, margin: '0 0 14px' }}>
        <span style={{ flex: 1, height: 1, background: line }} />
        <span style={{ fontSize: 12, color: muted }}>or</span>
        <span style={{ flex: 1, height: 1, background: line }} />
      </div>

      <div style={{ display: 'grid', gap: 8 }}>
        {usable.map((p) => {
          const mark = MARKS[p.provider] || { label: p.provider, glyph: '•', colour: '#fff', ink: '#111', border: '#ddd' };
          return (
            <button
              key={p.provider}
              type='button'
              onClick={() => start(p.provider)}
              style={{
                display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10,
                width: '100%', padding: '11px 14px', fontSize: 15, fontWeight: 600,
                borderRadius: 8, cursor: 'pointer', fontFamily: 'inherit',
                background: mark.colour, color: mark.ink, border: `1px solid ${mark.border}`,
              }}
            >
              <span style={{ fontWeight: 800, fontSize: 16 }}>{mark.glyph}</span>
              Continue with {mark.label}
            </button>
          );
        })}
      </div>

      {/* Said once, under the buttons, rather than on each.
          The customer is about to see a consent screen with somebody else's
          name on it, and finding that out on the screen itself reads as a
          phishing page. */}
      {usable.some((p) => p.owner === 'platform') && (
        <p style={{ fontSize: 12, color: muted, margin: '10px 0 0', lineHeight: 1.5 }}>
          Sign-in is handled for us by TechProOne, so you will see their name on the next screen.
        </p>
      )}
    </div>
  );
}
